import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { StaticRedirectAction } from '@/types/quiz';

const schema = z.object({
  name: z.string().min(1, 'Action name is required'),
  url: z.string().url('Please enter a valid URL'),
  openInNewTab: z.boolean(),
});

type FormData = z.infer<typeof schema>;

interface StaticRedirectActionFormProps {
  action?: StaticRedirectAction;
  onSave: (action: Omit<StaticRedirectAction, 'id'> & { id?: string }) => void;
  onCancel: () => void;
}

export function StaticRedirectActionForm({ action, onSave, onCancel }: StaticRedirectActionFormProps) {
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: action?.name || '',
      url: action?.url || '',
      openInNewTab: action?.openInNewTab || false,
    },
  });

  const openInNewTab = watch('openInNewTab');

  const onSubmit = (data: FormData) => {
    onSave({
      id: action?.id,
      type: 'static-redirect',
      name: data.name,
      url: data.url,
      openInNewTab: data.openInNewTab,
    });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(onSubmit)(e);
  };

  return (
    <form onSubmit={handleFormSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Action Name</Label>
        <Input
          id="name"
          placeholder="e.g., Redirect to thank you page"
          {...register('name')}
        />
        {errors.name && (
          <p className="text-sm text-destructive">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="url">Redirect URL</Label>
        <Input
          id="url"
          type="url"
          placeholder="https://example.com/thank-you"
          {...register('url')}
        />
        {errors.url && (
          <p className="text-sm text-destructive">{errors.url.message}</p>
        )}
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="openInNewTab"
          checked={openInNewTab}
          onCheckedChange={(checked) => setValue('openInNewTab', checked as boolean)}
        />
        <Label htmlFor="openInNewTab" className="cursor-pointer">
          Open in new tab
        </Label>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {action ? 'Update Action' : 'Add Action'}
        </Button>
      </div>
    </form>
  );
}
